import { Component, Inject, OnInit, Optional } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { TerminationService } from '../../../services/modules/termination-service';
import {environment} from '../../../../../environments/environment';
import {FechasService} from '../../../services/utils/fechas.service';

export interface WithdrawalRow {
  id: number;
  rowNumber: number;
  PersonalPhoto: string | null;

  fullName: string;
  documentType: string;
  documentNumber: string;

  employerName: string;
  employerDocumentType: string;
  employerDocumentNumber: string;

  terminationDate: string | null;
  created: boolean;
  failReason: string | null;
  processedDate: string | null;
}

export interface DialogGenerateWithdrawalsReportData {
  terminationDocumentId?: number;
  fileName?: string;
}

@Component({
  selector: 'app-dialog-generate-withdrawals-report',
  templateUrl: './dialog-generate-withdrawals-report.component.html',
  styleUrls: ['./dialog-generate-withdrawals-report.component.scss']
})
export class DialogGenerateWithdrawalsReportComponent implements OnInit {
  loading = false;
  loadError = false;

  docName = '';
  fileName = '';
  fileDate: string | null = null;
  fileUrl = '';

  reportAll: WithdrawalRow[] = [];
  reportFiltered: WithdrawalRow[] = [];
  pagedReport: WithdrawalRow[] = [];

  searchTerm = '';
  statusFilter: 'ALL' | 'CREATED' | 'NOT_CREATED' = 'ALL';

  pageIndex = 0;
  pageSize = 20;
  s3Bucket = '';

  readonly tableColumns: string[] = [
    'person',
    'employer',
    'terminationDate',
    'status',
    'failReason'
  ];

  constructor(
    private terminationService: TerminationService,
    private fechasService: FechasService,
    @Optional() public dialogRef: MatDialogRef<DialogGenerateWithdrawalsReportComponent>,
    @Optional() @Inject(MAT_DIALOG_DATA) public data: DialogGenerateWithdrawalsReportData
  ) { }

  ngOnInit(): void {
    this.s3Bucket = environment.api.s3Bucket;
    const terminationDocumentId = this.data && this.data.terminationDocumentId;

    if (!terminationDocumentId) {
      this.loadError = true;
      return;
    }

    this.loadReport(terminationDocumentId);
  }

  private loadReport(terminationDocumentId: number): void {
    this.loading = true;
    this.loadError = false;

    this.terminationService.getMassUploadTerminationsDocument(terminationDocumentId).subscribe({
      next: async (response: Blob | any) => {
        try {
          const parsed = await this.parseApiResponse(response);
          const rows: any[] = parsed && parsed.Rows ? parsed.Rows : [];

          this.fileUrl = parsed.FileUrl || '';
          this.fileName = (this.data && this.data.fileName) || this.extractFileName(parsed.FileUrl);
          this.docName = parsed.DocName || '';
          this.fileDate =
            parsed.StartDate ||
            parsed.UploadDate ||
            parsed.CreatedDate ||
            (rows.length > 0 ? rows[0].ProcessedDate : null);

          this.reportAll = rows.map(row => this.mapWithdrawalRow(row));
          this.updateSummaryAndFilters();

          this.loading = false;
        } catch {
          this.loadError = true;
          this.loading = false;
        }
      },
      error: () => {
        this.loadError = true;
        this.loading = false;
      }
    });
  }

  private async parseApiResponse(response: Blob | any): Promise<any> {
    if (response instanceof Blob) {
      const text = await response.text();
      return JSON.parse(text);
    }

    return response || {};
  }

  private mapWithdrawalRow(row: any): WithdrawalRow {
    return {
      id: row.Id || 0,
      rowNumber: row.RowNumber || 0,

      PersonalPhoto: row.PersonalPhoto,

      fullName: row.PersonalName || '—',
      documentType: row.PersonalDocumentType || '—',
      documentNumber: row.PersonalDocumentNumber || '—',

      employerName: row.EmployerName || '—',
      employerDocumentType: row.EmployerDocumentType || '—',
      employerDocumentNumber: row.EmployerDocumentNumber || '—',

      terminationDate: row.TerminationDate || null,
      created: this.isCreated(row.Created),
      failReason: row.FailReason || null,
      processedDate: row.ProcessedDate || null
    };
  }

  private isCreated(value: any): boolean {
    return value === true || value === 'true' || value === 1 || value === '1';
  }

  private extractFileName(fileUrl: string | null | undefined): string {
    if (!fileUrl) {
      return '—';
    }

    const cleanUrl = fileUrl.split('?')[0];
    const parts = cleanUrl.split('/');
    return parts[parts.length - 1] || fileUrl;
  }

  private normalizeText(value: string | null | undefined): string {
    return (value || '')
      .toString()
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .trim();
  }

  private updateSummaryAndFilters(): void {
    this.applyFilters(true);
  }

  private applyFilters(resetPage: boolean): void {
    if (resetPage) {
      this.pageIndex = 0;
    }

    const term = this.normalizeText(this.searchTerm);

    this.reportFiltered = this.reportAll.filter(row => {
      if (this.statusFilter === 'CREATED' && !row.created) {
        return false;
      }

      if (this.statusFilter === 'NOT_CREATED' && row.created) {
        return false;
      }

      if (!term) {
        return true;
      }

      const searchableText = this.normalizeText([
        row.fullName,
        row.documentType,
        row.documentNumber,
        row.employerName,
        row.employerDocumentType,
        row.employerDocumentNumber,
        row.failReason
      ].join(' '));

      return searchableText.indexOf(term) > -1;
    });

    if (this.pageIndex > this.totalPages - 1) {
      this.pageIndex = Math.max(0, this.totalPages - 1);
    }

    this.updatePagedReport();
  }

  private updatePagedReport(): void {
    const start = this.pageIndex * this.pageSize;
    this.pagedReport = this.reportFiltered.slice(start, start + this.pageSize);
  }

  get totalRows(): number {
    return this.reportAll.length;
  }

  get createdRows(): number {
    return this.reportAll.filter(row => row.created).length;
  }

  get notCreatedRows(): number {
    return this.reportAll.filter(row => !row.created).length;
  }

  get totalFiltered(): number {
    return this.reportFiltered.length;
  }

  get totalPages(): number {
    return Math.ceil(this.totalFiltered / this.pageSize);
  }

  get rangeStart(): number {
    if (this.totalFiltered === 0) {
      return 0;
    }

    return this.pageIndex * this.pageSize + 1;
  }

  get rangeEnd(): number {
    return Math.min(this.rangeStart + this.pageSize - 1, this.totalFiltered);
  }

  get visiblePages(): (number | '...')[] {
    const total = this.totalPages;
    const current = this.pageIndex + 1;
    const pages: (number | '...')[] = [];

    if (total <= 7) {
      for (let page = 1; page <= total; page++) {
        pages.push(page);
      }

      return pages;
    }

    pages.push(1);

    const start = Math.max(2, current - 1);
    const end = Math.min(total - 1, current + 1);

    if (start > 2) {
      pages.push('...');
    }

    for (let page = start; page <= end; page++) {
      pages.push(page);
    }

    if (end < total - 1) {
      pages.push('...');
    }

    pages.push(total);

    return pages;
  }

  onSearchChange(): void {
    this.applyFilters(true);
  }

  setStatusFilter(filter: 'ALL' | 'CREATED' | 'NOT_CREATED'): void {
    this.statusFilter = filter;
    this.applyFilters(true);
  }

  goToPage(page: number | '...'): void {
    if (page === '...') {
      return;
    }

    this.pageIndex = page - 1;
    this.updatePagedReport();
  }

  prevPage(): void {
    if (this.pageIndex === 0) {
      return;
    }

    this.pageIndex--;
    this.updatePagedReport();
  }

  nextPage(): void {
    if (this.pageIndex >= this.totalPages - 1) {
      return;
    }

    this.pageIndex++;
    this.updatePagedReport();
  }

  isCurrentPage(page: number | '...'): boolean {
    return page !== '...' && page - 1 === this.pageIndex;
  }

  openUploadedFile(): void {
    if (!this.fileUrl) {
      return;
    }
    window.open(this.s3Bucket + this.fileUrl, '_blank');
  }

  onImageError(row: WithdrawalRow): void {
    row.PersonalPhoto = null;
  }

  trackByRow(index: number, row: WithdrawalRow): number {
    return row.id || row.rowNumber || index;
  }

  close(): void {
    if (this.dialogRef) {
      this.dialogRef.close();
    }
  }

  showDateInfo(fileDate): string{
    return this.fechasService.getDateFormatR(fileDate);
  }

}
